// My Tasks in sections (WOS-BUNDLE; Work OS my-tasks.tsx, `api/me/{sections,bucket}`): Recently
// assigned, Today, Upcoming and Later, then the person's own sections, each a collapsible group.
// A row drags to another section or another place (`my.placeTask` with its position), and its
// "Move to…" menu does the same from the keyboard. "+ Add section" is always shown; an own
// section's header carries rename and delete on hover (delete says its tasks go back to Recently
// assigned). Every write shows at once and is undone if the server refuses.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isOperatorOnly } from "../company";
import { problemText } from "../settings/problem-text";
import { bundleKeys, type MySections, mySectionsQuery } from "./bundle-queries";
import { arrangeSections, placePosition, sectionOf, withPlacement } from "./my-sections";
import type { MoveTarget } from "./task-row-actions";

type Task = { id: string; due_at: string | null; number: number };

export type SectionGroup<T> = { id: string; title: string; tasks: T[]; actions?: ReactNode };

/** The sections' data and writes; `data` is null while loading or when the API lacks them. */
export function useMySections(api: Api, company: CompanyAccess) {
  const co = company.id;
  const qc = useQueryClient();
  const key = bundleKeys.sections(co);
  // OPERATOR-ONLY-UX: sections are a person member's (`my/sections` answers 403 otherwise).
  const q = useQuery({ ...mySectionsQuery(api, co), enabled: !isOperatorOnly(company) });
  const optimistic = (next: (d: MySections) => MySections) => {
    const before = qc.getQueryData<MySections | null>(key);
    if (before) qc.setQueryData(key, next(before));
    return before;
  };
  const settle = () => void qc.invalidateQueries({ queryKey: key });
  const place = useMutation({
    mutationFn: (v: { taskId: string; section: string; position?: number }) =>
      api.client.my.placeTask({
        params: { co, id: v.taskId },
        body: {
          section: v.section,
          ...(v.position !== undefined ? { position: v.position } : {}),
        },
      }),
    onMutate: (v) => ({
      before: optimistic((d) => withPlacement(d, v.taskId, v.section, v.position)),
    }),
    onError: (e, _v, c) => {
      if (c?.before) qc.setQueryData(key, c.before);
      toast.add({ title: problemText(e, "The task did not move."), type: "error" });
    },
    onSettled: settle,
  });
  const create = useMutation({
    mutationFn: (name: string) => api.client.mySections.create({ params: { co }, body: { name } }),
    onSuccess: (s) => toast.add({ title: `Section “${s.name}” added`, type: "success" }),
    onSettled: settle,
  });
  const rename = useMutation({
    mutationFn: (v: { id: string; name: string }) =>
      api.client.mySections.update({ params: { co, id: v.id }, body: { name: v.name } }),
    onMutate: (v) => ({
      before: optimistic((d) => ({
        ...d,
        sections: d.sections.map((s) => (s.id === v.id ? { ...s, name: v.name } : s)),
      })),
    }),
    onError: (e, _v, c) => {
      if (c?.before) qc.setQueryData(key, c.before);
      toast.add({ title: problemText(e, "The section was not renamed."), type: "error" });
    },
    onSettled: settle,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.client.mySections.delete({ params: { co, id } }),
    onMutate: (id) => ({
      before: optimistic((d) => ({
        sections: d.sections.filter((s) => s.id !== id),
        placements: d.placements.filter((p) => p.section !== id),
      })),
    }),
    onError: (e, _v, c) => {
      if (c?.before) qc.setQueryData(key, c.before);
      toast.add({ title: problemText(e, "The section was not deleted."), type: "error" });
    },
    onSettled: settle,
  });
  return { query: q, data: q.data ?? null, place, create, rename, remove };
}

/**
 * The sections as `TaskList` groups, the drop and "Move to…" handlers, the "+ Add section"
 * button and the dialogs they open.
 */
export function useSectionGroups<T extends Task>(
  s: ReturnType<typeof useMySections>,
  tasks: readonly T[],
): {
  groups: SectionGroup<T>[];
  onTaskDrop: (taskId: string, groupId: string, toIndex: number) => void;
  move: MoveTarget | undefined;
  addButton: ReactNode;
  dialogs: ReactNode;
} {
  const [naming, setNaming] = useState<{ id: string | null; name: string } | null>(null);
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const data = s.data;
  const arranged = data ? arrangeSections(data, tasks) : [];
  const groups = arranged.map((g) => ({
    id: g.id,
    title: g.name,
    tasks: g.tasks,
    ...(g.builtin
      ? {}
      : {
          actions: (
            <>
              <IconAction
                label={`Rename ${g.name}`}
                tip="Rename section"
                onClick={() => setNaming({ id: g.id, name: g.name })}
              >
                <PencilIcon aria-hidden="true" />
              </IconAction>
              <IconAction
                label={`Delete ${g.name}`}
                tip="Delete section"
                danger
                onClick={() => setDeleting({ id: g.id, name: g.name })}
              >
                <Trash2Icon aria-hidden="true" />
              </IconAction>
            </>
          ),
        }),
  }));
  const onTaskDrop = (taskId: string, groupId: string, toIndex: number) => {
    if (!data) return;
    const rows = arranged.find((g) => g.id === groupId)?.tasks ?? [];
    s.place.mutate({
      taskId,
      section: groupId,
      position: placePosition(data, rows, taskId, toIndex),
    });
  };
  const move: MoveTarget | undefined = data
    ? {
        sections: arranged.map((g) => ({ id: g.id, name: g.name })),
        current: (id) => sectionOf(data, id),
        onMove: (taskId, section) => s.place.mutate({ taskId, section }),
      }
    : undefined;
  const addButton = (
    <Button
      variant="ghost"
      size="sm"
      data-slot="add-section"
      className="mt-2 self-start text-muted-foreground hover:text-foreground"
      onClick={() => setNaming({ id: null, name: "" })}
    >
      <PlusIcon aria-hidden="true" />
      Add section
    </Button>
  );
  const dialogs = (
    <>
      {naming ? (
        <SectionNameDialog
          initial={naming.name}
          renaming={naming.id !== null}
          onClose={() => setNaming(null)}
          onSave={async (name) => {
            if (naming.id) s.rename.mutate({ id: naming.id, name });
            else await s.create.mutateAsync(name);
          }}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Its tasks go back to Recently assigned. Nothing else changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) s.remove.mutate(deleting.id);
                setDeleting(null);
              }}
            >
              Delete section
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
  return { groups, onTaskDrop, move, addButton, dialogs };
}

function IconAction({
  label,
  tip,
  danger,
  onClick,
  children,
}: {
  label: string;
  tip: string;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            className={danger ? "hover:text-danger-text" : undefined}
            onClick={onClick}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

function SectionNameDialog({
  initial,
  renaming,
  onClose,
  onSave,
}: {
  initial: string;
  renaming: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}) {
  const id = useId();
  const [name, setName] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) {
      setError("The section needs a name.");
      return;
    }
    setPending(true);
    try {
      await onSave(n);
      onClose();
    } catch (err) {
      setError(problemText(err, "The section was not saved."));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !pending && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <DialogHeader>
            <DialogTitle>{renaming ? "Rename section" : "New section"}</DialogTitle>
          </DialogHeader>
          <Field data-invalid={error ? true : undefined}>
            <FieldLabel htmlFor={id}>Name</FieldLabel>
            <Input
              id={id}
              value={name}
              maxLength={80}
              placeholder="Waiting on others"
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
            />
            {error ? (
              <p role="alert" className="text-xs text-danger-text">
                {error}
              </p>
            ) : null}
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {renaming ? "Save" : "Add section"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
